/**
 * 현장 사진을 서버로 보내기 전에 줄인다 — 긴 변 2,000px · JPEG.
 *
 * <p>현장 휴대폰 사진은 한 장에 4~8MB 다. 서버 상한이 10MB 라 한 장은 들어가지만 세 장을 연달아
 * 올리면 현장 LTE 로 1분이 넘고, 그 사이 사람은 「안 올라간다」 고 다시 누른다. 검측 증빙은
 * 2,000px 이면 지적 부위가 읽힌다 — 내부 촬영 가이드가 쓰는 크기와 같다.
 *
 * <p>줄이지 못하는 파일(HEIC 를 못 여는 브라우저 · 이미지가 아닌 것)은 <b>원본 그대로</b> 돌려준다.
 * 여기서 막으면 사진이 아예 안 올라가고, 서버가 크기와 종류를 다시 본다.
 */
const MAX_EDGE = 2000;
const QUALITY = 0.85;

export async function shrinkImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  }
  catch {
    return file;
  }

  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
  // 이미 작고 JPEG 면 손대지 않는다 — 다시 굽는 것은 화질만 잃는다.
  if (scale === 1 && file.type === 'image/jpeg') {
    bitmap.close();
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext('2d');
  if (context === null) {
    bitmap.close();
    return file;
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (blob === null) return file;

  const stem = file.name.replace(/\.[^.]+$/, '') || 'photo';
  return new File([blob], `${stem}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
}
